import React, { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { TextField } from '@mui/material';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { Body, Small } from '@/components/Typography';
import { TableCell, TableRow } from '@/elements/Table';
import styled from '@/styles';
import { accountabilityAPI } from '@/services/accountabilityAPI';
import { ViolationApprover } from '@/types/accountability';

const PAGE_PASSWORD = 'gravydevs123';
const AUTH_STORAGE_KEY = 'violation_approvers_page_unlocked';

const TabBar = styled('div', {
  display: 'inline-flex',
  backgroundColor: 'white',
  borderRadius: '$lg $lg 0 0',
  padding: '0 4px',
  borderBottom: '1px solid $colors$neutralLightest',
});

const TabLabel = styled('span', {
  display: 'inline-block',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 500,
  color: '$colors$textDefault',
  borderBottom: '2px solid $colors$themeDefault',
});

const headerFields = ['Name', 'Email', 'Added At', 'Action'];

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) {
    const errors = error.response?.data?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return errors[0];
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

export const ViolationApproversPage: React.FC = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');

  const [approvers, setApprovers] = useState<ViolationApprover[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const sortedApprovers = useMemo(
    () =>
      [...approvers].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [approvers],
  );

  const loadApprovers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountabilityAPI.getViolationApprovers();

      if (response.success) {
        setApprovers(response.violation_approvers || []);
      } else {
        setError(response.errors?.[0] || 'Failed to load violation approvers');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load violation approvers'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const isStoredUnlocked =
      sessionStorage.getItem(AUTH_STORAGE_KEY) === 'true';
    if (isStoredUnlocked) {
      setIsUnlocked(true);
    }
  }, []);

  useEffect(() => {
    if (!isUnlocked) return;

    loadApprovers();
  }, [isUnlocked]);

  const handleUnlock = () => {
    if (passwordInput !== PAGE_PASSWORD) {
      setError('Invalid password');
      setSuccess(null);
      return;
    }

    sessionStorage.setItem(AUTH_STORAGE_KEY, 'true');
    setIsUnlocked(true);
    setPasswordInput('');
    setError(null);
    setSuccess(null);
  };

  const handleAddApprover = async () => {
    if (!emailInput.trim()) {
      setError('Email is required');
      return;
    }

    try {
      setWorking(true);
      setError(null);
      setSuccess(null);

      const response = await accountabilityAPI.addViolationApprover(
        emailInput.trim(),
      );

      if (response.success) {
        setEmailInput('');
        setSuccess('Violation approver added');
        await loadApprovers();
      } else {
        setError(response.errors?.[0] || 'Failed to add violation approver');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to add violation approver'));
    } finally {
      setWorking(false);
    }
  };

  const handleDeleteApprover = async (id: number) => {
    try {
      setWorking(true);
      setError(null);
      setSuccess(null);

      const response = await accountabilityAPI.deleteViolationApprover(id);

      if (response.success) {
        setSuccess('Violation approver removed');
        await loadApprovers();
      } else {
        setError(response.errors?.[0] || 'Failed to delete violation approver');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete violation approver'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Page size="full" title="Tasks">
      <TabBar>
        <TabLabel>Violation Approvers</TabLabel>
      </TabBar>

      {(error || success) && (
        <Card>
          <Card.Section>
            <Stack vertical gap={8}>
              {error && (
                <Alert
                  description={error}
                  icon={faExclamationTriangle}
                  status="danger"
                  title="Error"
                />
              )}
              {success && (
                <Alert
                  description={success}
                  icon={faCheckCircle}
                  status="success"
                  title="Success"
                />
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}

      {!isUnlocked ? (
        <Card>
          <Card.Section>
            <Stack vertical gap={12}>
              <Body weight="medium">Enter page password to continue</Body>
              <Stack gap={12} align="end">
                <TextField
                  label="Password"
                  size="small"
                  type="password"
                  sx={{ minWidth: 300 }}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUnlock();
                    }
                  }}
                />
                <Button
                  a11yLabel="Unlock"
                  label="Unlock"
                  onClick={handleUnlock}
                />
              </Stack>
            </Stack>
          </Card.Section>
        </Card>
      ) : (
        <>
          <Card>
            <Card.Section>
              <Stack vertical gap={10}>
                <Small color="lighter">
                  Add approver by user email. Only listed users can clear
                  violations.
                </Small>
                <Stack gap={12} align="end">
                  <TextField
                    label="Email"
                    placeholder="name@example.com"
                    size="small"
                    sx={{ minWidth: 360 }}
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddApprover();
                      }
                    }}
                  />
                  <Button
                    a11yLabel="Add"
                    disabled={working}
                    isLoading={working}
                    label="Add"
                    onClick={handleAddApprover}
                  />
                </Stack>
              </Stack>
            </Card.Section>
          </Card>

          <Card>
            <Card.Section>
              {loading ? (
                <LoadingState overlayColor="white" />
              ) : (
                <Table>
                  <TableHeader fields={headerFields} />
                  <tbody>
                    {sortedApprovers.map((approver) => (
                      <TableRow key={approver.id}>
                        <TableCell>
                          <Body>{approver.name || '-'}</Body>
                        </TableCell>
                        <TableCell>
                          <Body>{approver.email}</Body>
                        </TableCell>
                        <TableCell>
                          <Body>
                            {new Date(approver.created_at).toLocaleString()}
                          </Body>
                        </TableCell>
                        <TableCell>
                          <Button
                            a11yLabel="Delete approver"
                            appearance="outline"
                            disabled={working}
                            label="Delete"
                            status="danger"
                            onClick={() => handleDeleteApprover(approver.id)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {sortedApprovers.length === 0 && (
                      <TableRow>
                        <TableCell>
                          <Body color="light">
                            No violation approvers found.
                          </Body>
                        </TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Section>
          </Card>
        </>
      )}
    </Page>
  );
};
